import { createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  adminCreateSubTopic,
  adminDeleteSubTopic,
  adminUpdateSubTopic,
} from '../SubTopicSlice';
import {
  adminApproveQuestion,
  adminCreateNewQuestion,
  adminUpdateQuestion,
  adminDeleteQuestion,
  adminCreateNewQuestions,
} from '../QuestionSlice';
import {
  createCourseAsAdmin,
  deleteCourseAsAdmin,
  updateCourseAsAdmin,
} from '../V2/CoursesSlice/adminApi';
import {
  finishSubmission,
  showAiHint,
  showSubmissionQuestionAnswer,
} from '../SubmissionSlice';
import { adminCreateQuiz, adminPublishQuiz } from '../QuizSlice';
import { startTrial } from '../Trial';
import { adminUpdateTopicSorting } from '../TopicSlice/TopicAdminApi';
import { requestCallback } from '../DiagnosticSlice';
import {
  bookCourseMeeting,
  cancelCourseTeamsSectionAsAdmin,
  cancelCourseTeamsSectionAsUser,
  cancelCoursZoomeSectionAsAdmin,
  cancelCoursZoomeSectionAsUser,
  createNewCourseResourceSectionAsAdmin,
  createNewCourseTeamSectionAsAdmin,
  createNewCourseZoomSectionAsAdmin,
  deleteCourseResourceSectionByIdAsAdmin,
  deleteCourseSectionByIdAsAdmin,
  deleteCourseTeamSectionByIdAsAdmin,
  deleteCourseTeamSectionByIdAsUser,
  deleteCourseZoomSectionByIdAsAdmin,
  deleteCourseZoomSectionByIdAsUser,
  rescheduleCourseTeamsSectionAsAdmin,
  rescheduleCourseTeamsSectionAsUser,
  rescheduleCoursZoomeSectionAsAdmin,
  rescheduleCoursZoomeSectionAsUser,
  updateCourseResourceSectionAsAdmin,
  updateCourseTeamsSectionAsAdmin,
  updateCourseZoomSectionAsAdmin,
} from '../V2/SectionSlice';
import {
  createCourseTutor,
  deleteCourseTutor,
  updateCourseTutor,
} from '../V2/CourseTutorsSlice';
import {
  createCourseStudent,
  deleteCourseStudent,
  updateCourseStudent,
} from '../V2/CourseStudentsSlice';
import { saveUserDetails } from '../AccessLimitSlice/AccessLimitApi';
import {
  adminDeleteCourseTemplate,
  adminUpdateCourseTemplate,
  createCourseTemplateAsAdmin,
} from '../V2/CourseTemplatesSlice/adminApi';
import { reportIssue, reportResources } from '../V2/ReportIssueSlice';
import { updateUserAvialabilityAsAdmin } from '../V2/UserAvailabilitySlice';
import { updateUserAvialabilityAsUser } from '../V2/UserAvailabilitySlice/api';
import { updateUser } from '../UserSlice/UserApi';
import {
  adminRespondMeetingRequest,
  adminUpdateMeetingRequest,
  adminUpdateMeetingRequestTutorPaymentStatus,
  MEETING_REQUEST_STATUSES,
} from '../V2/MeetingRequestsSlice';
import { createTrial } from '../V2/TrialsSlice';
import {
  createTrialAsAdmin,
  updateTrialAsAdmin,
} from '../V2/TrialsSlice/adminApi';
import {
  adminCreateLessonTutorFeedback,
  adminDeleteLessonTutorFeedback,
  adminUpdateLessonTutorFeedbacks,
  createLessonTutorFeedback,
  updateLessonTutorFeedbacks,
} from '../V2/LessonTutorFeedbacksSlice';
import {
  adminCreateLessonStudentFeedback,
  adminDeleteLessonStudentFeedback,
  adminUpdateLessonStudentFeedbacks,
  createLessonStudentFeedback,
  updateLessonStudentFeedbacks,
} from '../V2/LessonStudentFeedbacksSlice';
import {
  adminCreatePaymentAdjustment,
  adminTransferCourseCredits,
} from '../V2/SettingsSlice/adminApi';
import {
  adminCreateUserAdminNote,
  adminDeleteUserAdminNote,
  adminResendUserTempPassword,
  createUserAsAdmin,
} from '../UserSlice';
import {
  adminCreateInvoiceAdminNote,
  adminCreateStripeInvoice,
  adminDeleteInvoiceAdminNote,
  adminSendInvoiceMail,
  adminUpdateInvoice,
} from '../V2/InvoicesSlice';
import {
  adminCreateTransactionLogAdminNote,
  adminDeleteTransactionLogAdminNote,
} from '../V2/TransactionLogsSlice';
import {
  adminCreatePaymentHistoryAdminNote,
  adminDeletePaymentHistoryAdminNote,
} from '../V2/PaymentHistorySlice';
import {
  adminCreateLessonHistoryAdminNote,
  adminDeleteLessonHistoryAdminNote,
  adminRespondToLessonHistoryTutorPaymentConcern,
  adminUpdateLessonHistoryClassExtension,
  adminUpdateLessonHistoryTrialConversion,
  adminUpdateLessonHistoryTutorPaymentAdjustments,
  adminUpdateLessonHistoryTutorPaymentStatus,
  adminUpdateLessonHistoryTutorPenalty,
  lessonHistoryRaiseConcern,
} from '../V2/LessonHistorySlice';
import {
  adminCreateOneToOneTutorRateVersion,
  adminDeleteOneToOneTutorRateVersion,
  adminUpdateOneToOneTutorRateVersion,
} from '../V2/OneToOneTutorRateVersionsSlice';
import {
  adminCreateOneToOneTutorRateStudentType,
  adminDeleteOneToOneTutorRateStudentType,
  adminUpdateOneToOneTutorRateStudentType,
} from '../V2/OneToOneTutorRateStudentTypesSlice';
import {
  adminCreateOneToOneTutorRate,
  adminDeleteOneToOneTutorRate,
  adminUpdateOneToOneTutorRate,
} from '../V2/OneToOneTutorRatesSlice';
import {
  adminCreateGroupTutorRateVersion,
  adminDeleteGroupTutorRateVersion,
  adminUpdateGroupTutorRateVersion,
} from '../V2/GroupTutorRateVersionsSlice';
import {
  adminCreateGroupTutorRateCourseType,
  adminUpdateGroupTutorRateCourseType,
  adminDeleteGroupTutorRateCourseType,
} from '../V2/GroupTutorRateCourseTypesSlice';
import {
  adminCreateGroupTutorRate,
  adminDeleteGroupTutorRate,
  adminUpdateGroupTutorRate,
} from '../V2/GroupTutorRatesSlice';
import {
  adminCreateFlexiblePlan,
  adminDeleteFlexiblePlan,
  adminUpdateFlexiblePlan,
} from '../V2/FlexiblePlansSlice';
import {
  adminCreateSubscriptionPlan,
  adminDeleteSubscriptionPlan,
  adminUpdateSubscriptionPlan,
} from '../V2/SubscriptionPlansSlice';
import {
  adminCreateGroup,
  adminDeleteGroup,
  adminUpdateGroup,
} from '../V2/GroupsSlice';
import {
  adminCreateCoursePermissionTemplate,
  adminDeleteCoursePermissionTemplate,
  adminUpdateCoursePermissionTemplate,
} from '../V2/CoursePermissionTemplatesSlice';
import {
  adminCreateStudentFlexibleRateVersion,
  adminDeleteStudentFlexibleRateVersion,
  adminUpdateStudentFlexibleRateVersion,
} from '../V2/StudentFlexibleRateVersionsSlice';
import {
  adminCreateStudentFlexibleRate,
  adminDeleteStudentFlexibleRate,
  adminUpdateStudentFlexibleRate,
} from '../V2/StudentFlexibleRatesSlice';
import {
  adminCreateStudentSubscriptionRateVersion,
  adminDeleteStudentSubscriptionRateVersion,
  adminUpdateStudentSubscriptionRateVersion,
} from '../V2/StudentSubscriptionRateVersionsSlice';
import {
  adminCreateStudentSubscriptionRate,
  adminDeleteStudentSubscriptionRate,
  adminUpdateStudentSubscriptionRate,
} from '../V2/StudentSubscriptionRatesSlice';
import {
  adminCreateCoursePricingTemplate,
  adminDeleteCoursePricingTemplate,
  adminUpdateCoursePricingTemplate,
} from '../V2/CoursePricingTemplatesSlice';
import {
  adminCreateRole,
  adminDeleteRole,
  adminUpdateRole,
} from '../V2/RolesSlice';
import {
  adminCreateMetaTag,
  adminDeleteMetaTag,
  adminUpdateMetaTag,
} from '../V2/MetaTagsSlice/adminApi';
import {
  adminCreatePenaltyWaiver,
  adminRespondToPenaltyAppeal,
  adminUpdatePenaltyWaiverTutorPaymentStatus,
} from '../V2/PenaltyWaiverSlice/adminApi';
import { createPenaltyAppealRequest } from '../V2/PenaltyWaiverSlice';
import { adminGenerateSitemap } from '../V2/SitemapSlice/adminApi';
import { adminCreateManualAdjustment } from '../V2/ManualAdjustmentsSlice';
import {
  adminCreateNewStructuredQuestion,
  approveStructuredEssayQuestion,
  deleteStructuredEssayQuestion,
  updateStructuredEssayQuestion,
} from '../StructuredEssayQuestionSlice';
import {
  adminSyncTutorSorting,
  adminUpdateTutorSorting,
} from '../V2/TutorSortingSlice/adminApi';
import {
  sendCourseCreditReminderNotification,
  updateCourse,
} from '../V2/CoursesSlice';
import {
  adminCreateTutorRole,
  adminDeleteTutorRole,
  adminUpdateTutorRole,
} from '../V2/TutorRolesSlice/adminApi';
import { notifyUserWaiting } from '../V2/WaitingRoomSlice';
import { createCustomQuiz, deleteCustomQuiz } from '../V2/CustomQuizSlice';

export interface IinitialState {
  visible: boolean;
  message: string;
}

const initialState: IinitialState = {
  visible: false,
  message: '',
};

export const SuccessSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    resetMessage: (state) => {
      state.visible = false;
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Subtopic Slice
      .addCase(adminCreateSubTopic.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Sub topic Added';
      })
      .addCase(adminDeleteSubTopic.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Sub topic Deleted';
      })
      .addCase(adminUpdateSubTopic.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Sub topic Updated';
      })
      .addCase(adminApproveQuestion.fulfilled, (state, action) => {
        if (action.payload.isApproved) {
          state.visible = true;
          state.message = 'Question approved';
        } else {
          state.visible = true;
          state.message = 'Question un-approved';
        }
      })
      // Question Slice
      .addCase(adminCreateNewQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question Created';
      })
      .addCase(adminUpdateQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question Updated';
      })
      .addCase(adminDeleteQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question Deleted';
      })
      .addCase(adminCreateNewQuestions.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Questions Created';
      })
      // Quiz Slice
      .addCase(adminCreateQuiz.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiz Created';
      })
      .addCase(adminPublishQuiz.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiz updated';
      })
      // Submission Slice
      .addCase(finishSubmission.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiz Completed';
      })
      .addCase(showSubmissionQuestionAnswer.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Answer is now Visible';
      })
      .addCase(showAiHint.fulfilled, (state) => {
        state.visible = true;
        state.message = 'AI Hint is now Visible';
      })
      // Trial Slice
      .addCase(startTrial.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiztopiya 1 day Free Trial Started!';
      })
      // Topic Slice
      .addCase(adminUpdateTopicSorting.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Topics updated!';
      })
      // Report Resources Slice
      .addCase(reportResources.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Issue reported successfully!';
      })
      // Diagnostic Slice
      .addCase(requestCallback.fulfilled, (state) => {
        state.visible = true;
        state.message =
          'Thank you for requesting callback. Our team will reach out to you shortly';
      })
      // AccessLimit Slice
      .addCase(saveUserDetails.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Details updated successfully!';
      })
      // =====================================================================
      // V2
      .addCase(createCourseAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course Created!';
      })
      .addCase(updateCourseAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course Updated!';
      })
      .addCase(deleteCourseAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course Deleted!';
      })
      .addCase(deleteCourseSectionByIdAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Section Deleted!';
      })
      .addCase(deleteCourseZoomSectionByIdAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Zoom Meeting Deleted!';
      })
      .addCase(deleteCourseTeamSectionByIdAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Team Meeting Deleted!';
      })
      .addCase(deleteCourseResourceSectionByIdAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Resource Deleted!';
      })
      .addCase(createCourseTutor.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor Added!';
      })
      .addCase(deleteCourseTutor.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor Deleted!';
      })
      .addCase(updateCourseTutor.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor Updated!';
      })
      .addCase(createCourseStudent.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student Added!';
      })
      .addCase(deleteCourseStudent.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student Deleted!';
      })
      .addCase(updateCourseStudent.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student Updated!';
      })
      .addCase(createCourseTemplateAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course Template Saved!';
      })
      .addCase(createNewCourseZoomSectionAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Zoom Meeting Added!';
      })
      .addCase(createNewCourseTeamSectionAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Teams Meeting Added!';
      })
      .addCase(cancelCoursZoomeSectionAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        let message = 'Zoom Meeting Updated!';

        if (result.zoomRequest) {
          if (result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED) {
            message = 'Zoom Meeting Cancelled!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
          ) {
            message = 'Cancellation Request Rejected!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
          ) {
            message = 'Cancellation Request Sent!';
          }
        }

        state.visible = true;
        state.message = message;
      })
      .addCase(cancelCourseTeamsSectionAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        let message = 'Zoom Meeting Updated!';

        if (result.zoomRequest) {
          if (result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED) {
            message = 'Zoom Meeting Cancelled!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
          ) {
            message = 'Cancellation Request Rejected!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
          ) {
            message = 'Cancellation Request Sent!';
          }
        }

        state.visible = true;
        state.message = message;
      })
      .addCase(createNewCourseResourceSectionAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Resource Added!';
      })
      .addCase(updateCourseResourceSectionAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Resource Updated!';
      })
      .addCase(cancelCoursZoomeSectionAsUser.fulfilled, (state, action) => {
        const result = action.payload;

        let message = 'Lesson Updated!';

        if (result.zoomRequest) {
          if (result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED) {
            message = 'Lesson Cancelled!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
          ) {
            message = 'Cancellation Request Rejected!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
          ) {
            message = 'Cancellation Request Sent!';
          }
        }

        state.visible = true;
        state.message = message;
      })
      .addCase(cancelCourseTeamsSectionAsUser.fulfilled, (state, action) => {
        const result = action.payload;

        let message = 'Lesson Updated!';

        if (result.zoomRequest) {
          if (result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED) {
            message = 'Lesson Cancelled!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
          ) {
            message = 'Cancellation Request Rejected!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
          ) {
            message = 'Cancellation Request Sent!';
          }
        }

        state.visible = true;
        state.message = message;
      })
      .addCase(updateUserAvialabilityAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'User Availability Data Updated!';
      })
      .addCase(updateUserAvialabilityAsUser.fulfilled, (state) => {
        state.visible = true;
        state.message = 'User Availability Data Updated!';
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Account Updated!';
      })
      .addCase(rescheduleCoursZoomeSectionAsUser.fulfilled, (state, action) => {
        const result = action.payload;

        let message = 'Lesson Updated!';

        if (result.zoomRequest) {
          if (result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED) {
            message = 'Lesson Rescheduled!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
          ) {
            message = 'Reschedule Request Rejected!';
          } else if (
            result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
          ) {
            message = 'Reschedule Request Sent!';
          }
        }

        state.visible = true;
        state.message = message;
      })
      .addCase(
        rescheduleCourseTeamsSectionAsUser.fulfilled,
        (state, action) => {
          const result = action.payload;

          let message = 'Lesson Updated!';

          if (result.zoomRequest) {
            if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED
            ) {
              message = 'Lesson Rescheduled!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
            ) {
              message = 'Reschedule Request Rejected!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
            ) {
              message = 'Reschedule Request Sent!';
            }
          }

          state.visible = true;
          state.message = message;
        }
      )
      .addCase(
        rescheduleCoursZoomeSectionAsAdmin.fulfilled,
        (state, action) => {
          const result = action.payload;

          let message = 'Lesson Updated!';

          if (result.zoomRequest) {
            if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED
            ) {
              message = 'Lesson Rescheduled!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
            ) {
              message = 'Reschedule Request Rejected!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
            ) {
              message = 'Reschedule Request Sent!';
            }
          }

          state.visible = true;
          state.message = message;
        }
      )
      .addCase(
        rescheduleCourseTeamsSectionAsAdmin.fulfilled,
        (state, action) => {
          const result = action.payload;

          let message = 'Lesson Updated!';

          if (result.zoomRequest) {
            if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED
            ) {
              message = 'Lesson Rescheduled!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.REJECTED
            ) {
              message = 'Reschedule Request Rejected!';
            } else if (
              result.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
            ) {
              message = 'Reschedule Request Sent!';
            }
          }

          state.visible = true;
          state.message = message;
        }
      )
      .addCase(adminRespondMeetingRequest.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Lesson Request Responded!';
      })
      .addCase(adminUpdateMeetingRequest.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Lesson Request Updated!';
      })
      .addCase(reportIssue.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Issue reported successfully!';
      })
      .addCase(createTrial.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Trial class booked successfully!';
      })
      .addCase(createTrialAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Trial class booked successfully!';
      })
      .addCase(updateTrialAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Trial class Updated!';
      })
      .addCase(adminCreateLessonStudentFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student feedback created!';
      })
      .addCase(createLessonStudentFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student feedback created!';
      })
      .addCase(adminUpdateLessonStudentFeedbacks.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student feedback updated!';
      })
      .addCase(updateLessonStudentFeedbacks.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student feedback updated!';
      })
      .addCase(adminDeleteLessonStudentFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student feedback deleted!';
      })
      .addCase(adminCreateLessonTutorFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor feedback created!';
      })
      .addCase(createLessonTutorFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor feedback created!';
      })
      .addCase(adminUpdateLessonTutorFeedbacks.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor feedback updated!';
      })
      .addCase(updateLessonTutorFeedbacks.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor feedback updated!';
      })
      .addCase(adminDeleteLessonTutorFeedback.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor feedback deleted!';
      })
      .addCase(adminCreateFlexiblePlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Flexible plan added!';
      })
      .addCase(adminUpdateFlexiblePlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Flexible plan updated!';
      })
      .addCase(adminDeleteFlexiblePlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Flexible plan deleted!';
      })
      .addCase(adminCreateSubscriptionPlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Subscription plan deleted!';
      })
      .addCase(adminUpdateSubscriptionPlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Subscription plan updated!';
      })
      .addCase(adminDeleteSubscriptionPlan.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Subscription plan deleted!';
      })
      .addCase(adminCreateGroup.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group created!';
      })
      .addCase(adminUpdateGroup.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group updated!';
      })
      .addCase(adminDeleteGroup.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group deleted!';
      })
      .addCase(adminCreateRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Role created!';
      })
      .addCase(adminUpdateRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Role updated!';
      })
      .addCase(adminDeleteRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Role deleted!';
      })
      .addCase(adminCreateCoursePermissionTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course permission template created!';
      })
      .addCase(adminUpdateCoursePermissionTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course permission template updated!';
      })
      .addCase(adminDeleteCoursePermissionTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course permission template deleted!';
      })
      .addCase(adminCreateStudentFlexibleRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate version created!';
      })
      .addCase(adminUpdateStudentFlexibleRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate version updated!';
      })
      .addCase(adminDeleteStudentFlexibleRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate version deleted!';
      })
      .addCase(adminCreateStudentFlexibleRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate created!';
      })
      .addCase(adminUpdateStudentFlexibleRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate updated!';
      })
      .addCase(adminDeleteStudentFlexibleRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student flexible rate deleted!';
      })
      .addCase(adminCreateStudentSubscriptionRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate version created!';
      })
      .addCase(adminUpdateStudentSubscriptionRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate version updated!';
      })
      .addCase(adminDeleteStudentSubscriptionRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate version deleted!';
      })
      .addCase(adminCreateStudentSubscriptionRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate deleted!';
      })
      .addCase(adminUpdateStudentSubscriptionRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate updated!';
      })
      .addCase(adminDeleteStudentSubscriptionRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Student subscription rate deleted!';
      })
      .addCase(adminCreateCoursePricingTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course pricing template created!';
      })
      .addCase(adminUpdateCoursePricingTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course pricing template updated!';
      })
      .addCase(adminDeleteCoursePricingTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course pricing template deleted!';
      })
      .addCase(createUserAsAdmin.fulfilled, (state) => {
        state.visible = true;
        state.message = 'User account created!';
      })
      .addCase(adminCreateStripeInvoice.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Invoice Sent!';
      })
      .addCase(adminUpdateInvoice.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Invoice Updated!';
      })
      .addCase(adminSendInvoiceMail.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Mail Sent!';
      })
      .addCase(adminCreateInvoiceAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Created!';
      })
      .addCase(adminDeleteInvoiceAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Deleted!';
      })
      .addCase(adminCreatePaymentAdjustment.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Payment Adjustment Created!';
      })
      .addCase(adminCreateTransactionLogAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Created!';
      })
      .addCase(adminDeleteTransactionLogAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Deleted!';
      })
      .addCase(adminCreatePaymentHistoryAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Created!';
      })
      .addCase(adminDeletePaymentHistoryAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Deleted!';
      })
      .addCase(adminCreateUserAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Created!';
      })
      .addCase(adminDeleteUserAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Deleted!';
      })
      .addCase(adminCreateLessonHistoryAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Created!';
      })
      .addCase(adminDeleteLessonHistoryAdminNote.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Note Deleted!';
      })
      .addCase(adminCreateOneToOneTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate version created!';
      })
      .addCase(adminUpdateOneToOneTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate version updated!';
      })
      .addCase(adminDeleteOneToOneTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate version deleted!';
      })
      .addCase(adminCreateOneToOneTutorRateStudentType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate student type created!';
      })
      .addCase(adminUpdateOneToOneTutorRateStudentType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate student type updated!';
      })
      .addCase(adminDeleteOneToOneTutorRateStudentType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate student type deleted!';
      })
      .addCase(adminCreateOneToOneTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate created!';
      })
      .addCase(adminUpdateOneToOneTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate updated!';
      })
      .addCase(adminDeleteOneToOneTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'One to one tutor rate deleted!';
      })
      .addCase(adminCreateGroupTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate version created!';
      })
      .addCase(adminDeleteGroupTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate version deleted!';
      })
      .addCase(adminUpdateGroupTutorRateVersion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate version updated!';
      })
      .addCase(adminCreateGroupTutorRateCourseType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate course type created!';
      })
      .addCase(adminUpdateGroupTutorRateCourseType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate course type updated!';
      })
      .addCase(adminDeleteGroupTutorRateCourseType.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate course type deleted!';
      })
      .addCase(adminCreateGroupTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate created!';
      })
      .addCase(adminUpdateGroupTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate updated!';
      })
      .addCase(adminDeleteGroupTutorRate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Group tutor rate deleted!';
      })
      .addCase(
        adminUpdateLessonHistoryTutorPaymentStatus.fulfilled,
        (state) => {
          state.visible = true;
          state.message = 'Tutor payment status updated!';
        }
      )
      .addCase(updateCourseZoomSectionAsAdmin.fulfilled, (state, action) => {
        state.visible = true;
        state.message = 'Zoom Meeting Updated!';
      })
      .addCase(updateCourseTeamsSectionAsAdmin.fulfilled, (state, action) => {
        state.visible = true;
        state.message = 'Teams Meeting Updated!';
      })
      .addCase(
        adminUpdateLessonHistoryTutorPaymentAdjustments.fulfilled,
        (state) => {
          state.visible = true;
          state.message = 'Adjustments updated!';
        }
      )
      .addCase(lessonHistoryRaiseConcern.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Concern raised!';
      })
      .addCase(
        adminRespondToLessonHistoryTutorPaymentConcern.fulfilled,
        (state) => {
          state.visible = true;
          state.message = 'Tutor payment concern responded!';
        }
      )
      .addCase(adminUpdateLessonHistoryClassExtension.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Class extension updated!';
      })
      .addCase(adminUpdateLessonHistoryTutorPenalty.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor penalty updated!';
      })
      .addCase(adminUpdateLessonHistoryTrialConversion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Trial conversion updated!';
      })
      .addCase(adminCreateMetaTag.fulfilled, (state) => {
        state.visible = true;
        state.message = 'MetaTag created!';
      })
      .addCase(adminUpdateMetaTag.fulfilled, (state) => {
        state.visible = true;
        state.message = 'MetaTag updated!';
      })
      .addCase(adminDeleteMetaTag.fulfilled, (state) => {
        state.visible = true;
        state.message = 'MetaTag deleted!';
      })
      .addCase(adminCreatePenaltyWaiver.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Penalty waiver created!';
      })
      .addCase(createPenaltyAppealRequest.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Penalty appeal request sent!';
      })
      .addCase(adminGenerateSitemap.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Sitemap generate started!';
      })
      .addCase(
        adminUpdatePenaltyWaiverTutorPaymentStatus.fulfilled,
        (state) => {
          state.visible = true;
          state.message = 'Payment status updated!';
        }
      )
      .addCase(
        adminUpdateMeetingRequestTutorPaymentStatus.fulfilled,
        (state) => {
          state.visible = true;
          state.message = 'Tutor payment status updated!';
        }
      )
      .addCase(adminRespondToPenaltyAppeal.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Appeal responded!';
      })
      .addCase(adminCreateManualAdjustment.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Manual adjustment created!';
      })
      .addCase(adminUpdateCourseTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course template updated!';
      })
      .addCase(adminDeleteCourseTemplate.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course template deleted!';
      })
      .addCase(adminResendUserTempPassword.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Temporary password sent!';
      })
      .addCase(bookCourseMeeting.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Lesson booked successfully!';
      })
      .addCase(notifyUserWaiting.fulfilled, (state, account) => {
        state.visible = true;
        state.message = account.payload.message;
      })
      // Structured Essay
      .addCase(adminCreateNewStructuredQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question created!';
      })
      .addCase(updateStructuredEssayQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question is updated!';
      })
      .addCase(deleteStructuredEssayQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question is deleted!';
      })
      .addCase(approveStructuredEssayQuestion.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Question approval status updated successfully';
      })
      .addCase(adminUpdateTutorSorting.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor sorting updated successfully!';
      })
      .addCase(adminSyncTutorSorting.fulfilled, (state) => {
        state.visible = true;
        state.message =
          'Tutor sorting sync started. Tutor list will update in a few minutes.';
      })
      .addCase(updateCourse.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course Updated!';
      })
      .addCase(adminCreateTutorRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor role created!';
      })
      .addCase(adminUpdateTutorRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor role updated!';
      })
      .addCase(adminDeleteTutorRole.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Tutor role deleted!';
      })
      .addCase(deleteCourseTeamSectionByIdAsUser.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Team Meeting Deleted!';
      })
      .addCase(deleteCourseZoomSectionByIdAsUser.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Zoom Meeting Deleted!';
      })
      .addCase(createCustomQuiz.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiz created successfully!';
      })
      .addCase(deleteCustomQuiz.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Quiz deleted successfully!';
      })
      .addCase(sendCourseCreditReminderNotification.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Reminder sent!';
      })
      .addCase(adminTransferCourseCredits.fulfilled, (state) => {
        state.visible = true;
        state.message = 'Course credits transfered!';
      });
  },
});

export const { resetMessage } = SuccessSlice.actions;

export const getSuccessState = (state: RootState): RootState['success'] =>
  state.success;

export default SuccessSlice.reducer;
